/**
 * React hook for managing saved searches backed by the users DynamoDB table
 * via the GetUserPreference / UpdateUserPreference GraphQL operations.
 *
 * Replaces the localStorage-based savedSearchStore for authenticated users.
 *
 * Design decisions:
 * - Unrecognized entries (written by a newer client or with a schema this
 *   version cannot parse) are PRESERVED on writes. Only the entries this
 *   client understands are shown in the UI and subject to eviction/removal.
 * - Mutation failures are caught and surfaced via the returned boolean so
 *   callers can show feedback or keep the modal open.
 * - Writes are blocked until the first successful data load to prevent
 *   overwriting server-side data with an empty snapshot. Background
 *   refetches (cache-and-network) do NOT block writes.
 * - Writes are SERIALIZED through a promise queue keyed by `preferenceKey` at
 *   MODULE scope (not per hook instance), and each operation reads the current
 *   list straight from the Apollo cache (`readLatest`) rather than from a
 *   render-lagged ref. This matters because the same key has multiple hook
 *   instances (e.g. `SearchPage` takes `save`; its `SavedSearchesBar` takes
 *   `remove`) — a per-instance queue would not serialize a save in one against a
 *   remove in the other, so both would read the same pre-mutation snapshot and
 *   the second write would clobber the first (resurrecting a just-removed entry
 *   or losing a just-saved one). A module-level per-key queue makes every write
 *   to a key run one-at-a-time and see the previous write's committed result,
 *   regardless of which component issued it.
 */

import { useCallback, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  UserPreferenceKey,
  useGetUserPreferenceQuery,
  useUpdateUserPreferenceMutation,
  GetUserPreferenceDocument,
  GetUserPreferenceQuery,
} from 'src/generated/graphql';

import {
  SavedSearchEntry,
  DEFAULT_MAX_SAVED_SEARCHES,
} from 'src/common/savedSearches/savedSearchStore';
import { useTranslation, type TranslateFn } from 'src/i18n/shim/useTranslation';

interface UseUserPreferencesOptions<TState> {
  /** Which preference key this hook manages. */
  preferenceKey: UserPreferenceKey;
  /** Validates one raw stored state, returning null when it is unusable. */
  parseState: (raw: unknown) => TState | null;
  /**
   * Suggested label, used when the caller supplies none.
   *
   * The hook supplies `t` from its own `useTranslation()` rather than each
   * generator closing over a module-level one, which would resolve against
   * whatever locale was loaded when the module was first imported instead of
   * the one being rendered.
   */
  generateLabel: (t: TranslateFn, state: TState) => string;
  /** Maximum entries before the oldest is evicted. Defaults to 5. */
  maxEntries?: number;
}

/** Result from a save attempt. */
export interface SaveResult {
  /** Whether the entry was successfully persisted. */
  success: boolean;
  /** If an exact duplicate (same state AND same name) already exists. */
  duplicateLabel?: string;
  /** If true, an existing entry with the same state was renamed instead of creating a new one. */
  renamed?: boolean;
  /** True when a save was attempted before the initial data load completed. */
  notReady?: boolean;
  /** True when the initial preference load failed (network/auth error). */
  loadFailed?: boolean;
}

interface UseUserPreferencesResult<TState> {
  /** All saved entries, parsed and validated. */
  entries: SavedSearchEntry<TState>[];
  /** Whether the query is still loading. */
  loading: boolean;
  /** Save a new entry. Detects duplicates by state; evicts oldest if at capacity. */
  save: (state: TState, name?: string) => Promise<SaveResult>;
  /** Remove an entry by ID. Returns false on failure or if still loading. */
  remove: (id: string) => Promise<boolean>;
  /** Refetch from the server. */
  refetch: () => void;
}

/** Recognized entries, their original raw JSON, and unrecognized raw values. */
interface SplitValues<TState> {
  entries: SavedSearchEntry<TState>[];
  rawById: Map<string, string>;
  unrecognizedRaw: string[];
}

/**
 * Attempts to parse a raw JSON string into a SavedSearchEntry.
 * Returns the entry if valid, or null if it cannot be understood.
 */
function tryParseEntry<TState>(
  raw: string,
  parseState: (raw: unknown) => TState | null,
): SavedSearchEntry<TState> | null {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.id !== 'string' ||
      typeof parsed.label !== 'string'
    ) {
      return null;
    }
    const state = parseState(parsed.state);
    if (!state) return null;

    return {
      id: parsed.id as string,
      label: parsed.label as string,
      state,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Splits raw API values into recognized entries (retaining each one's original
 * raw JSON, to avoid lossy round-trips through `parseState` on write) and
 * unrecognized-but-valid-JSON values (preserved verbatim for forward-compat).
 * Non-JSON values are discarded — the backend rejects them.
 */
function splitRawValues<TState>(
  rawValues: readonly string[],
  parseState: (raw: unknown) => TState | null,
): SplitValues<TState> {
  const recognized: SavedSearchEntry<TState>[] = [];
  const rawById = new Map<string, string>();
  const unrecognizedRaw: string[] = [];

  for (const raw of rawValues) {
    const entry = tryParseEntry(raw, parseState);
    if (entry) {
      recognized.push(entry);
      rawById.set(entry.id, raw);
    } else {
      try {
        JSON.parse(raw);
        unrecognizedRaw.push(raw);
      } catch {
        // Not valid JSON — silently discard
      }
    }
  }
  return { entries: recognized, rawById, unrecognizedRaw };
}

/**
 * Produces a stable, canonical string for equality comparison of saved-search
 * state, independent of object-key insertion order and multiselect array order.
 *
 * `JSON.stringify` is order-sensitive — `{a,b}` vs `{b,a}` and `[x,y]` vs
 * `[y,x]` stringify differently. Normal UI interactions (e.g. selecting filters
 * in a different order, or reordered multiselect values) would otherwise dodge
 * duplicate/rename detection, letting a logically-identical search be re-saved
 * under a new name and, at the capacity limit, evict an unrelated oldest entry.
 *
 * Canonicalization: object keys are sorted recursively; arrays whose elements
 * are all primitive are sorted (they model order-insensitive multiselect sets).
 * Arrays containing objects keep their order, since element order there may be
 * semantically meaningful.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    const items = value.map(canonicalize);
    const allPrimitive = items.every(
      (item) => item === null || typeof item !== 'object',
    );
    return allPrimitive
      ? [...items].sort((a, b) => String(a).localeCompare(String(b)))
      : items;
  }
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = canonicalize((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }
  return value;
}

/** Order-insensitive stable serialization used only for state equality. */
function stableStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

/**
 * Module-level write queues, one promise chain per `preferenceKey`. Shared
 * across ALL `useUserPreferences` instances for that key, so writes issued by
 * different components (e.g. a save from `SearchPage` and a remove from its
 * `SavedSearchesBar`) serialize against each other rather than racing on the
 * same pre-mutation cache snapshot. Bounded — one entry per distinct key.
 */
const opChainsByKey = new Map<UserPreferenceKey, Promise<unknown>>();

/**
 * Hook that manages a list-shaped user preference through the backend API.
 * Each value in the list is a serialized JSON document representing one saved search entry.
 */
export function useUserPreferences<TState>(
  options: UseUserPreferencesOptions<TState>,
): UseUserPreferencesResult<TState> {
  const {
    preferenceKey,
    parseState,
    generateLabel,
    maxEntries = DEFAULT_MAX_SAVED_SEARCHES,
  } = options;

  const client = useApolloClient();
  const { t } = useTranslation();

  const { data, loading, error, refetch } = useGetUserPreferenceQuery({
    variables: { preferenceKey },
    fetchPolicy: 'cache-and-network',
  });

  const [updatePreference] = useUpdateUserPreferenceMutation();

  /** The rendered entries — recognized, parsed, and validated. */
  const entries = useMemo(
    () =>
      splitRawValues(data?.getUserPreference?.values ?? [], parseState).entries,
    [data, parseState],
  );

  // Track whether initial data has loaded (not `loading`, which is also true during background refetches)
  const hasDataRef = useRef(false);
  hasDataRef.current = Boolean(data?.getUserPreference);
  // Track whether initial load errored (distinct from "not yet loaded")
  const loadErrorRef = useRef(false);
  loadErrorRef.current = Boolean(error && !loading && !hasDataRef.current);

  /**
   * Reads the freshest committed list straight from the Apollo cache and
   * re-splits it, so an operation queued behind another sees the previous
   * write's `update` result rather than a render-lagged snapshot.
   */
  const readLatest = useCallback((): SplitValues<TState> => {
    const cached = client.readQuery<GetUserPreferenceQuery>({
      query: GetUserPreferenceDocument,
      variables: { preferenceKey },
    });
    return splitRawValues(cached?.getUserPreference?.values ?? [], parseState);
  }, [client, preferenceKey, parseState]);

  /**
   * Serializes writes for this `preferenceKey` across ALL hook instances so
   * concurrent save/remove calls (even from different components) run one-at-a-
   * time. Each enqueued op runs after the previous settles (success OR failure),
   * so it can `readLatest()` the prior write's committed result instead of
   * racing on a stale read.
   */
  const enqueue = useCallback(
    <T>(op: () => Promise<T>): Promise<T> => {
      const prev = opChainsByKey.get(preferenceKey) ?? Promise.resolve();
      const run = prev.then(op, op);
      opChainsByKey.set(
        preferenceKey,
        run.catch(() => undefined),
      );
      return run;
    },
    [preferenceKey],
  );

  /** Persist entries to the backend. Existing entries reuse their original raw
   *  string to avoid lossy normalization; only new/renamed entries are
   *  serialized fresh (via freshIds). Unrecognized raw values are appended
   *  verbatim so a newer client's entries survive this client's writes.
   *
   *  Note: This is a last-writer-wins update. Concurrent writes from multiple
   *  tabs/devices will overwrite each other. Acceptable for user preferences
   *  (low write frequency, single user). */
  const persistEntries = useCallback(
    async (
      newEntries: SavedSearchEntry<TState>[],
      rawById: Map<string, string>,
      unrecognizedRaw: string[],
      freshIds?: Set<string>,
    ): Promise<boolean> => {
      const serialized = [
        ...newEntries.map((entry) => {
          if (freshIds?.has(entry.id)) return JSON.stringify(entry);
          return rawById.get(entry.id) ?? JSON.stringify(entry);
        }),
        ...unrecognizedRaw,
      ];
      try {
        await updatePreference({
          variables: {
            input: {
              preferenceKey,
              values: serialized,
            },
          },
          optimisticResponse: {
            updateUserPreference: {
              __typename: 'UserPreferenceResponse',
              preferenceKey,
              values: serialized,
            },
          },
          update: (cache, { data: mutationData }) => {
            cache.writeQuery({
              query: GetUserPreferenceDocument,
              variables: { preferenceKey },
              data: {
                getUserPreference: mutationData!.updateUserPreference,
              },
            });
          },
        });
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useUserPreferences] Failed to persist entries:', err);
        return false;
      }
    },
    [preferenceKey, updatePreference],
  );

  const save = useCallback(
    (state: TState, name?: string): Promise<SaveResult> =>
      enqueue(async () => {
        if (loadErrorRef.current) return { success: false, loadFailed: true };
        if (!hasDataRef.current) return { success: false, notReady: true };

        // Read the latest committed list so a queued save sees prior writes.
        const { entries: current, rawById, unrecognizedRaw } = readLatest();

        const trimmed = name?.trim();
        const label =
          trimmed && trimmed.length > 0 ? trimmed : generateLabel(t, state);

        // Check for an existing entry with the same state (ignoring label).
        // Normalize the incoming state the same way stored entries were
        // normalized by parseState, so the comparison is apples-to-apples.
        const normalizedState = parseState(state);
        if (!normalizedState) return { success: false };
        const normalizedJson = stableStringify(normalizedState);
        const existing = current.find(
          (e) => stableStringify(e.state) === normalizedJson,
        );
        if (existing) {
          if (existing.label === label) {
            // Exact duplicate (same state AND same name) — nothing to do
            return { success: false, duplicateLabel: existing.label };
          }
          // Same state but different name — rename and move to end (newest position)
          const renamed = { ...existing, label, savedAt: Date.now() };
          const updated = [
            ...current.filter((e) => e.id !== existing.id),
            renamed,
          ];
          const persisted = await persistEntries(
            updated,
            rawById,
            unrecognizedRaw,
            new Set([existing.id]),
          );
          return { success: persisted, renamed: true };
        }

        const newEntry: SavedSearchEntry<TState> = {
          id: crypto.randomUUID(),
          label,
          state: normalizedState,
          savedAt: Date.now(),
        };

        const updated = [...current, newEntry];
        while (updated.length > maxEntries) {
          updated.shift();
        }

        const persisted = await persistEntries(
          updated,
          rawById,
          unrecognizedRaw,
        );
        return { success: persisted };
      }),
    [
      enqueue,
      readLatest,
      maxEntries,
      generateLabel,
      persistEntries,
      parseState,
      t,
    ],
  );

  const remove = useCallback(
    (id: string): Promise<boolean> =>
      enqueue(async () => {
        if (!hasDataRef.current) return false;

        // Read the latest committed list so a queued remove sees prior writes.
        const { entries: current, rawById, unrecognizedRaw } = readLatest();
        const updated = current.filter((entry) => entry.id !== id);
        return persistEntries(updated, rawById, unrecognizedRaw);
      }),
    [enqueue, readLatest, persistEntries],
  );

  return {
    entries,
    loading,
    save,
    remove,
    refetch,
  };
}
